import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { AdminNotification, Paginated, UnreadCount } from './types'

/**
 * Contador de la campanita.
 *
 * El backend ya difunde cada notificación por el canal privado
 * `admin.notifications` (Módulo 6). Mientras no haya un servidor de websockets
 * aprovisionado (Reverb o Pusher), el panel se mantiene al día por sondeo:
 * es menos inmediato pero no depende de infraestructura extra.
 */
export function useUnreadCount(intervalMs = 30_000) {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async (): Promise<UnreadCount> => {
      const { data } = await api.get<UnreadCount>('/admin/notifications/unread-count')
      return data
    },
    refetchInterval: intervalMs,
  })
}

export function useNotifications(unreadOnly = false, perPage = 15) {
  return useQuery({
    queryKey: ['notifications', { unreadOnly, perPage }],
    queryFn: async (): Promise<Paginated<AdminNotification>> => {
      const { data } = await api.get<Paginated<AdminNotification>>('/admin/notifications', {
        params: { unread: unreadOnly ? 1 : undefined, per_page: perPage },
      })
      return data
    },
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uuid: string) => {
      await api.post(`/admin/notifications/${uuid}/read`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.post('/admin/notifications/read-all')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
